/**
 * Role derivation from the Cognito `cognito:groups` ID-token claim.
 *
 * COSMETIC ONLY. This drives the role label and which affordances the UI shows — it is NEVER the
 * authorization gate. A client can read/edit its own token payload, so nothing here may be trusted
 * for access control; the server (aws-jwt-verify + per-route 403 / pricing-field stripping) remains
 * the real gate.
 *
 * Group strings MUST mirror the server exactly:
 *   - `admin`           — stores mutations + catalog sync (rest-api routes/stores.ts, routes/catalog.ts)
 *   - `pricing-admin`   — unlocks INTERNAL pricing fields (rest-api services/pricing-config-view.ts)
 *   - `inventory-admin` — blank-inventory WRITES (rest-api routes/require-inventory-write.ts) + the
 *     ledger EXPORT (rest-api routes/export.ts — API only since sidebar PR-4 removed the dashboard
 *     Export page, owner decision D2). Enforced server-side since #107/#9, so it is a cosmetic role here.
 * A user in no group (absent/empty claim) is the least-privileged 'Staff'.
 */
export const ADMIN_GROUP = 'admin';
export const PRICING_ADMIN_GROUP = 'pricing-admin';
export const INVENTORY_ADMIN_GROUP = 'inventory-admin';

export type Role = {
  /** Human label for the user menu. */
  label: string;
  /** In the `admin` group — may run admin actions (catalog sync, store mutations). */
  isAdmin: boolean;
  /** In the `pricing-admin` group — sees INTERNAL pricing fields. */
  isPricingAdmin: boolean;
  /**
   * In the `inventory-admin` group (literal membership, like the flags above). The server gates
   * blank-inventory writes + the ledger export on `admin` OR `inventory-admin`, so a caller gates
   * those affordances with `isAdmin || isInventoryAdmin` — or the derived `isInventoryWrite` below.
   */
  isInventoryAdmin: boolean;
  /**
   * Derived: may write blank inventory (Pull/Adjust/Count/Par/Add-stock/Receive) — `isAdmin ||
   * isInventoryAdmin`, mirroring the server's `requireInventoryWrite`/`requireCostView` gate exactly.
   * The single flag every inventory write affordance disables on, so the OR isn't hand-repeated (and
   * can't drift) at each call site. COSMETIC — the server 403 is still the real gate.
   */
  isInventoryWrite: boolean;
};

/**
 * Narrow the raw `cognito:groups` claim to a `string[]`. Amplify types the JWT payload with a
 * `JsonObject` index signature, so the claim is loosely typed — filter to strings rather than an
 * unchecked cast (CLAUDE.md § 1.3). Missing/malformed → `[]`.
 */
export function parseGroups(claim: unknown): string[] {
  return Array.isArray(claim) ? claim.filter((g): g is string => typeof g === 'string') : [];
}

/** Derive the display role from the user's groups. Label rank: admin > pricing-admin > inventory-admin. */
export function deriveRole(groups: readonly string[] | undefined): Role {
  const g = groups ?? [];
  const isAdmin = g.includes(ADMIN_GROUP);
  const isPricingAdmin = g.includes(PRICING_ADMIN_GROUP);
  const isInventoryAdmin = g.includes(INVENTORY_ADMIN_GROUP);
  const label = isAdmin
    ? 'Admin'
    : isPricingAdmin
      ? 'Pricing admin'
      : isInventoryAdmin
        ? 'Inventory admin'
        : 'Staff';
  return {
    label,
    isAdmin,
    isPricingAdmin,
    isInventoryAdmin,
    isInventoryWrite: isAdmin || isInventoryAdmin,
  };
}
